import React, { FC, useCallback, useEffect, useState } from "react";

interface ImageGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: { url: string; id_room_image: number }[];
  currentImageIndex: number;
  onImageChange: (index: number) => void;
}

const ImageGalleryModal: FC<ImageGalleryModalProps> = ({
  isOpen,
  onClose,
  images,
  currentImageIndex,
  onImageChange,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(currentImageIndex);

  useEffect(() => {
    setSelectedIndex(currentImageIndex);
  }, [currentImageIndex]);

  const handlePrevious = useCallback(() => {
    const newIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
    setSelectedIndex(newIndex);
    onImageChange(newIndex);
  }, [selectedIndex, images.length, onImageChange]);

  const handleNext = useCallback(() => {
    const newIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
    setSelectedIndex(newIndex);
    onImageChange(newIndex);
  }, [selectedIndex, images.length, onImageChange]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowLeft") {
        handlePrevious();
      } else if (event.key === "ArrowRight") {
        handleNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, handlePrevious, handleNext, onClose]);

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
    onImageChange(index);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-95"
      onClick={onClose}
    >
      {/* Close Button - positioned on the black background */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 z-20 text-white hover:text-gray-300 transition-colors"
        style={{ fontSize: "32px" }}
      >
        ×
      </button>

      {/* Modal Content - Full Screen */}
      <div
        className="relative w-full h-full flex flex-col items-center justify-center p-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Main Image Container */}
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={images[selectedIndex]?.url || "/api/placeholder/800/600"}
            alt={`Room ${selectedIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                style={{
                  backgroundColor: "white",
                  borderRadius: "100%",
                  color: "#BF5934",
                  width: "64px",
                  height: "64px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  padding: "0",
                }}
              >
                <span
                  style={{
                    fontSize: "64px",
                    fontWeight: "300",
                    lineHeight: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 18,
                    marginRight: 4,
                  }}
                >
                  ‹
                </span>
              </button>
              <button
                onClick={handleNext}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
                style={{
                  backgroundColor: "white",
                  borderRadius: "100%",
                  color: "#BF5934",
                  width: "64px",
                  height: "64px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  padding: "0",
                }}
              >
                <span
                  style={{
                    fontSize: "64px",
                    fontWeight: "300",
                    lineHeight: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 18,
                    marginLeft: 4,
                  }}
                >
                  ›
                </span>
              </button>
            </>
          )}
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
            <div className="flex space-x-3 px-4">
              {images.map((image, index) => (
                <button
                  key={image.id_room_image}
                  onClick={() => handleThumbnailClick(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden border-2 transition-all ${
                    index === selectedIndex
                      ? "border-white shadow-lg"
                      : "border-gray-500 hover:border-gray-300"
                  }`}
                >
                  <img
                    src={image.url || "/api/placeholder/100/100"}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGalleryModal;
